import React from 'react'
import styled from 'styled-components'
import onClickOutside from 'react-onclickoutside'
import i18n from 'live/services/i18n'
import { MarkersData } from '../api'
import Markers from '../index'
import { formPositioner } from '../visual/positioners'
import dateService from 'live/services/date'
import { PointsList } from './annotations/CurvesList'
import ReactDOM from 'react-dom'

const STEP_SIZES = {
    edit: { width: 230, height: 240 },
    menu: { width: 165, height: 66 },
    delete: { width: 230, height: 125 }
}

function WidgetRootPortal(props: { widgetId: number; children: React.ReactNode }): React.ReactPortal {
    const widgetRoot = document.querySelector('.widget-' + props.widgetId)

    if (widgetRoot == null) return null

    return ReactDOM.createPortal(props.children, widgetRoot)
}

const MarkersForm: React.FC<{ widgetId: number }> = (props: { widgetId: number }) => {
    const rootRef = React.useRef<HTMLDivElement>()
    const [open, setOpen] = React.useState(false)
    const [positionParams, setPositionParams] = React.useState<Record<string, any>>()
    const [step, setStep] = React.useState<'menu' | 'delete' | 'edit'>('menu')
    const [marker, setMarker] = React.useState<MarkersData>(null)
    const [save, setSave] = React.useState<(marker: MarkersData) => Promise<void | MarkersData>>(null)
    const [deleteMarker, setDeleteMarker] = React.useState<(marker: MarkersData) => Promise<void | Error>>(null)

    const close = (): void => {
        setStep('menu')
        setPositionParams(undefined)
        setOpen(false)
    }

    // Finding out the best position to render
    // this form inside the widget taking into
    // account the position of the marker.
    const position = React.useMemo(() => {
        if (!positionParams?.externalPositions) return undefined

        const { externalPositions, widgetPosition, chartPosition } = positionParams

        const pos = formPositioner(
            externalPositions.marker,
            STEP_SIZES[step],
            externalPositions.chartPlotBox,
            externalPositions.isInverted
        )

        pos.left += chartPosition.left - widgetPosition.left
        pos.top += chartPosition.top - widgetPosition.top

        pos.top = Math.max(pos.top, 5)

        // There is simple not enough space in either side: put on the start
        if (pos.left + STEP_SIZES[step].width > widgetPosition.width) {
            pos.left = chartPosition.left - widgetPosition.left
        }

        return pos
    }, [step, positionParams])

    const left = position?.left
    const top = position?.top
    const width = STEP_SIZES[step].width

    React.useEffect(() => {
        const undo = Markers.onShowMarkerDetailsFor(props.widgetId, (data, posParams, save, remove) => {
            const widgetPosition = rootRef.current.parentElement.getBoundingClientRect()
            const chartPosition = rootRef.current.parentElement.querySelector('svg')?.getBoundingClientRect?.()

            setPositionParams({ widgetPosition, chartPosition, externalPositions: posParams })
            setOpen(true)
            setMarker(data)
            setSave(() => save)
            setDeleteMarker(() => remove)
        })

        return () => {
            undo()
        }
    }, [])

    // https://github.com/Pomax/react-onclickoutside/issues/329#issuecomment-613443002
    MarkersForm['handleClickOutside_' + props.widgetId] = () => close()

    return (
        <WidgetRootPortal widgetId={props.widgetId}>
            <Root
                ref={rootRef}
                className={open ? 'open' : ''}
                style={{ left: left || '', top: top || '', width: width || '' }}
            >
                {step === 'menu' && <Menu close={close} setStep={setStep} marker={marker} />}
                {step === 'delete' && <Delete close={close} setStep={setStep} marker={marker} delete={deleteMarker} />}
                {step === 'edit' && (
                    <Edit close={close} setStep={setStep} marker={marker} save={save} widgetId={props.widgetId} />
                )}
            </Root>
        </WidgetRootPortal>
    )
}

const clickOutsideConfig = {
    handleClickOutside: ({ props }) => MarkersForm['handleClickOutside_' + props.widgetId]
}

export default onClickOutside(MarkersForm, clickOutsideConfig)

interface WizardProps {
    widgetId?: number
    close: () => void
    setStep: (name: 'menu' | 'delete' | 'edit') => void
    marker: MarkersData
    save?: (marker: MarkersData) => Promise<void | MarkersData>
    delete?: (marker: MarkersData) => Promise<void | Error>
}

const Menu: React.FC<WizardProps> = (props: WizardProps) => {
    return (
        <StyledMenu>
            <div onClick={() => props.setStep('edit')}>
                <i className="far fa-edit" /> {i18n('Edit note text')}
            </div>
            <div onClick={() => props.setStep('delete')} className="remove">
                <i className="far fa-trash-alt" /> {i18n('Delete marker')}
            </div>
        </StyledMenu>
    )
}

const Delete: React.FC<WizardProps> = (props: WizardProps) => {
    return (
        <StyledDelete>
            <div className="message">{i18n('marker_widget_action_msg_3')}.</div>
            <div className="actions">
                <button
                    className="confirm"
                    onClick={() => {
                        props.delete(props.marker)
                        props.close()
                    }}
                >
                    {i18n('Confirm')}
                </button>
                <button className="cancel" onClick={() => props.close()}>
                    {i18n('Cancel')}
                </button>
            </div>
        </StyledDelete>
    )
}

const Edit: React.FC<WizardProps> = (props: WizardProps) => {
    const msg = props.marker?.message || ''
    const myInput = React.useRef<HTMLTextAreaElement>()
    const widgets = props.marker.widgets.filter(item => item?.widgetId === props.widgetId)
    const points = widgets[0]?.points || []

    return (
        <StyledEdit>
            <div className="x-value">
                {props.marker.target?.spanType === 'time'
                    ? dateService.formatDateTime(props.marker.value)
                    : props.marker.value}
            </div>
            <div className="curves">
                <PointsList points={points} />
            </div>
            <div className="form-data">
                <div>{i18n('Take notes on this marker')}:</div>
                <textarea ref={myInput} defaultValue={props.marker?.message || ''}></textarea>
            </div>
            <div className="actions">
                <button
                    className="confirm"
                    onClick={() => {
                        const text = myInput?.current?.value || msg

                        props.save({ ...props.marker, message: text })
                        props.close()
                    }}
                >
                    {i18n('Update marker')}
                </button>
            </div>
        </StyledEdit>
    )
}

const Root = styled.div`
    display: none;
    position: absolute;
    border: inset 1px #28334a;
    background: #364159;
    box-shadow: 0 3px 25px 0 rgba(23, 31, 51, 0.5);
    z-index: 14;
    &.open {
        display: block;
    }

    .actions {
        button {
            color: white;
            border: 1px solid rgb(96, 106, 128);
            font-weight: bold;
            font-family: 'SourceSansPro-Semibold';
            height: 24px;
            padding: 0px;
            margin: 0px;
            margin-right: 7px;
            border-radius: 3px;

            &.confirm {
                background: #37a3ef;
                border-color: #37a3ef;
            }
            &.cancel {
                background: #364159;
            }
        }
    }
`

const StyledMenu = styled.div`
    width: 100%;
    height: 66px;
    padding: 12px 9px;
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    div {
        font-size: 14px;
        color: white;
        cursor: pointer;
        i {
            margin-right: 7px;
            color: #37a3ef;
        }
        i.fa-trash-alt {
            margin-right: 10.5px;
        }
    }
`
const StyledDelete = styled.div`
    /* width: 230px; */
    width: 100%;
    min-height: 125px;
    padding: 10px 15px;

    .message {
        color: white;
        font-size: 14px;
        margin-bottom: 10px;
    }

    .actions {
        display: flex;
    }

    .actions button {
        max-width: 65px;
        width: 50%;
    }
`

const StyledEdit = styled.div`
    width: 100%;
    height: 240px;
    max-height: 240px;
    display: flex;
    flex-flow: column;
    overflow: auto;

    .x-value {
        color: ${({ theme }) => theme['gray-300']};
        padding: 5px 10px;
        font-weight: bold;
        border-bottom: solid 1px ${({ theme }) => theme['darker-bg']};
    }

    .curves {
        padding: 5px 10px;
        border-bottom: solid 1px ${({ theme }) => theme['darker-bg']};
        .curve {
            border: none;
            color: white;
            font-size: 13px;
        }
    }

    button {
        width: 105px;
    }

    .form-data,
    .actions {
        padding: 10px;
    }

    .form-data {
        flex: 1;
        min-height: 100px;
        padding-bottom: 5px;
        div {
            font-size: 13px;
            font-weight: bold;
            line-height: 16px;
            color: rgb(255, 255, 255);
            margin-bottom: 5px;
        }

        textarea {
            resize: none;
            height: calc(100% - 21px);
            background: rgb(40, 51, 74);
            border: inset 1px rgb(23, 32, 51);
            outline: none;
            color: white;
            box-shadow: none;
            line-height: 16px;
        }
    }

    .actions {
        padding-top: 5px;
        padding-bottom: 5px;
        border-top: solid 1px #172033;
    }
`
